import { get } from './api';

export function stripNoteTitleCode(title, number) {
  const t = (title || '').trim();
  const code = number != null && String(number).trim() !== '' ? String(number).trim() : '';
  if (!code) return t;
  if (t.endsWith(` - ${code}`)) return t.slice(0, -(code.length + 3)).trim();
  if (t.endsWith(`-${code}`)) return t.slice(0, -(code.length + 1)).trim();
  return t;
}

function transformChaptersData(chapters) {
  if (!chapters || !Array.isArray(chapters)) return [];

  return chapters.map((chapter) => {
    const transformedChapter = {
      id: chapter.id || null,
      title: chapter.title || '',
      index_number: chapter.index_number || null,
    };

    if (chapter.notes && Array.isArray(chapter.notes) && chapter.notes.length > 0) {
      transformedChapter.children = chapter.notes.map((note) => {
        const transformedNote = {
          id: note.id || null,
          title: stripNoteTitleCode(note.title, note.number),
          index_number: note.index_number || null,
          number: note.number || null,
        };

        if (note.sub_notes && Array.isArray(note.sub_notes) && note.sub_notes.length > 0) {
          transformedNote.children = note.sub_notes.map((subNote) => ({
            id: subNote.id || null,
            title: stripNoteTitleCode(subNote.title, subNote.number),
            index_number: subNote.index_number || null,
            number: subNote.number || null,
          }));
        }

        return transformedNote;
      });
    }

    return transformedChapter;
  });
}

function transformConditionNotesData(notes) {
  if (!notes || !Array.isArray(notes)) return [];

  return notes.map((note) => {
    const item = {
      id: note.id || null,
      title: stripNoteTitleCode(note.title, note.number),
      chapter_index: note.chapter_index,
      index_number: note.index_number,
      number: note.number || null,
    };

    if (note.sub_notes && Array.isArray(note.sub_notes) && note.sub_notes.length > 0) {
      item.children = note.sub_notes.map((sub) => ({
        id: sub.id || null,
        title: stripNoteTitleCode(sub.title, sub.number),
        index_number: sub.index_number,
        number: sub.number || null,
      }));
    }

    return item;
  });
}

/** Flat navigable sequence for Areas (chapters → notes → subnotes). */
export function flattenChapterNavSequence(chapters) {
  const seq = [];
  if (!Array.isArray(chapters)) return seq;

  chapters.forEach((chapter, chapterIndex) => {
    const notes = chapter.children || [];
    notes.forEach((note, noteIndex) => {
      if (note.children && note.children.length) {
        note.children.forEach((sub, subIndex) => {
          if (sub.id == null) return;
          seq.push({
            id: sub.id,
            type: 'subnotes',
            title: sub.title,
            parentTitle: chapter.title || '',
            childTitle: note.title || '',
            parentIndex: chapterIndex,
            childIndex: noteIndex,
            grandIndex: subIndex,
          });
        });
      } else if (note.id != null) {
        seq.push({
          id: note.id,
          type: 'notes',
          title: note.title,
          parentTitle: chapter.title || '',
          childTitle: '',
          parentIndex: chapterIndex,
          childIndex: noteIndex,
          grandIndex: null,
        });
      }
    });
  });

  return seq;
}

/** Flat navigable sequence for Conditions (notes → subnotes). */
export function flattenConditionNavSequence(notes) {
  const seq = [];
  if (!Array.isArray(notes)) return seq;

  notes.forEach((note, parentIndex) => {
    if (note.children && note.children.length) {
      note.children.forEach((sub, childIndex) => {
        if (sub.id == null) return;
        seq.push({
          id: sub.id,
          type: 'subnotes',
          title: sub.title,
          parentTitle: note.title || '',
          childTitle: '',
          parentIndex,
          childIndex,
          grandIndex: null,
        });
      });
    } else if (note.id != null) {
      seq.push({
        id: note.id,
        type: 'notes',
        title: note.title,
        parentTitle: '',
        childTitle: '',
        parentIndex,
        childIndex: null,
        grandIndex: null,
      });
    }
  });

  return seq;
}

export function findNavIndex(sequence, id, type) {
  if (!sequence || !sequence.length || id == null || id === '') return -1;
  const sid = String(id);
  const t = type === 'subnotes' ? 'subnotes' : 'notes';
  return sequence.findIndex((item) => String(item.id) === sid && item.type === t);
}

function columnKeyFromSection(section) {
  const s = (section || '').toLowerCase();
  if (s.includes('professional')) return 'professional';
  return 'clinical';
}

async function fetchColumnItems(columnKey, viewMode) {
  if (viewMode === 'conditions') {
    const areaId = columnKey === 'professional' ? 2 : 1;
    const res = await get(`/condition-notes?area_id=${areaId}`);
    return transformConditionNotesData(res.data || []);
  }

  const res = await get('/notesclient');
  const data = res.data || {};
  const chapters = columnKey === 'professional'
    ? data.chaptersProfessioanl
    : data.chaptersClinical;
  return transformChaptersData(chapters);
}

/**
 * Build prev/next sequence for the list the user came from (clinical/professional × areas/conditions).
 * If the current note is not in the preferred column, tries the other column.
 */
export async function fetchNotesNavSequence({ columnKey, viewMode, noteId, noteType }) {
  const preferred = columnKey || 'clinical';
  const columns = preferred === 'professional' ? ['professional', 'clinical'] : ['clinical', 'professional'];

  for (const col of columns) {
    try {
      const items = await fetchColumnItems(col, viewMode);
      const sequence = viewMode === 'conditions'
        ? flattenConditionNavSequence(items)
        : flattenChapterNavSequence(items);
      const index = findNavIndex(sequence, noteId, noteType);
      if (index >= 0) {
        return { sequence, index, columnKey: col, viewMode };
      }
    } catch (e) {
      /* try next column */
    }
  }

  return { sequence: [], index: -1, columnKey: preferred, viewMode };
}

export function resolveNotesNavContext() {
  let columnKey = '';
  try {
    columnKey = localStorage.getItem('notesRestore_column') || '';
  } catch (e) {
    /* ignore */
  }
  if (!columnKey) {
    try {
      columnKey = columnKeyFromSection(localStorage.getItem('notesSection'));
    } catch (e) {
      columnKey = 'clinical';
    }
  }

  let viewMode = 'areas';
  try {
    viewMode = localStorage.getItem('notesViewMode') || 'areas';
  } catch (e) {
    /* ignore */
  }

  return {
    columnKey: columnKey === 'professional' ? 'professional' : 'clinical',
    viewMode: viewMode === 'conditions' ? 'conditions' : 'areas',
  };
}

export function persistNotesNavRestoreState(item, { columnKey, viewMode }) {
  if (!item) return;
  try {
    localStorage.setItem('notesRestore_column', columnKey);
    localStorage.setItem('notesRestore_listMode', viewMode === 'conditions' ? 'flat' : 'chapter');
    localStorage.setItem('notesViewMode', viewMode);
    localStorage.setItem('notesListRestoreViewMode', viewMode);
    localStorage.setItem(
      'notesListRestoreSection',
      viewMode === 'conditions' ? 'BY CONDITIONS' : 'BY AREAS',
    );
    localStorage.setItem('notesRestore_parentIndex', String(item.parentIndex));
    if (item.childIndex != null && item.childIndex !== '') {
      localStorage.setItem('notesRestore_childIndex', String(item.childIndex));
    } else {
      localStorage.removeItem('notesRestore_childIndex');
    }
    if (item.grandIndex != null && item.grandIndex !== '') {
      localStorage.setItem('notesRestore_grandIndex', String(item.grandIndex));
    } else {
      localStorage.removeItem('notesRestore_grandIndex');
    }
    localStorage.setItem('notestitle', item.title || '');
    localStorage.setItem('notestype', item.type === 'subnotes' ? 'subnotes' : 'notes');
    localStorage.setItem('notesParentTitle', item.parentTitle || '');
    localStorage.setItem('notesChildTitle', item.childTitle || '');
  } catch (e) {
    /* ignore */
  }
}
